/**
 * Shared chrome (header + footer) for marketing pages that aren't the
 * homepage itself — /pricing, /for-pm, /for-developers, /security, etc.
 *
 * The homepage `Homepage.tsx` keeps its own bespoke header/footer because
 * its nav uses in-page anchor links (#problem, #mechanic, etc.) that
 * don't apply to dedicated landing variants. Marketing variants share
 * the brand + cross-page nav + auth CTA defined here.
 *
 * Visual system: same warm-paper + editorial palette as Homepage. CSS
 * tokens live in homepage.css (already loaded on these routes since
 * MarketingShell imports it).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { LanguageToggle } from './LanguageToggle';
import { RelatedLinks } from './RelatedLinks';
import {
  type MarketingLocale,
  LOCALE_PREFIX,
  LOCALE_HREFLANG,
} from '../i18n/marketingLocales';
import './homepage.css';

interface Props {
  /** Page-specific title — also sets `<title>` for SEO. */
  documentTitle?: string;
  /** Optional `<meta name="description">` set for SEO. */
  documentDescription?: string;
  /** Which top-level marketing route is currently active (drives nav
   *  highlight). Pass undefined on the homepage where this shell isn't
   *  used. */
  activeRoute?: '/pricing' | '/for-pm' | '/for-developers';
  /** Current page locale. Drives hreflang emission and tells Google
   *  which alternate this page IS. Default 'en'. */
  currentLocale?: MarketingLocale;
  /** Canonical path WITHOUT locale prefix, e.g. '/comparisons/notion-ai'.
   *  Used to compute hreflang alternates per locale (en → `{path}`,
   *  zh → `/zh{path}`, ja → `/ja{path}`, …). Skip emission if undefined. */
  hreflangPath?: string;
  /** Locales this page genuinely has a translation in — drives which
   *  hreflang alternates we emit. Default `['en','zh']` (the original EN+ZH
   *  pairs). A single-locale page (e.g. a JA-only reframe bridge with no EN
   *  sibling) passes `['ja']`, so we emit a self-canonical and NO bogus
   *  alternates pointing at URLs that don't exist. */
  hreflangLocales?: MarketingLocale[];
  children: ReactNode;
}

const SITE_ORIGIN = 'https://draftlize.com';

export function MarketingShell({
  documentTitle,
  documentDescription,
  activeRoute,
  currentLocale = 'en',
  hreflangPath,
  hreflangLocales = ['en', 'zh'],
  children,
}: Props) {
  const { t } = useTranslation();

  // doc/07 PR-31 polish: probe auth so the header CTA flips for users
  // who already have an account. Unauthed visitors see "Start free"
  // → / (the app, which hands new accounts $5 in credit). Authed users
  // see "OPEN APP →" pointing at /. Probing on every marketing page mount is fine —
  // /api/me is a single indexed Postgres read (~50ms) and the probe
  // result drives nothing critical, just a label swap. Errors leave
  // the default (unauthed) state, so a flaky API doesn't kill the CTA.
  const [authed, setAuthed] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.me()
      .then(() => {
        if (!cancelled) setAuthed(true);
      })
      .catch(() => {
        if (!cancelled) setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Set document.title + meta description per route. Search crawlers
  // execute JS in 2026 (Googlebot since 2019, Bingbot since 2024), so
  // these reach the indexed snippet. We restore on unmount so SPA
  // back-navigation doesn't leave a stale title on the homepage.
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content');

    if (documentTitle) document.title = documentTitle;
    if (documentDescription) {
      let metaDesc = document.querySelector(
        'meta[name="description"]',
      ) as HTMLMetaElement | null;
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = documentDescription;
    }

    return () => {
      document.title = prevTitle;
      if (prevDesc !== undefined && prevDesc !== null) {
        document
          .querySelector('meta[name="description"]')
          ?.setAttribute('content', prevDesc);
      }
    };
  }, [documentTitle, documentDescription]);

  // SEO: hreflang link tags + canonical. Tells Google "this URL has
  // alternate-language versions at these other URLs" so non-EN searches
  // route to the zh page instead of the en page being half-translated
  // in the snippet. Bidirectional confirmation is required — both en and
  // zh pages must announce each other — so the en pages also call this
  // with currentLocale='en'.
  //
  // Reuse a stable id ('hreflang-' prefix) so re-renders replace rather
  // than accumulate; cleanup on unmount removes them entirely (back-nav
  // to homepage doesn't leave stale alternates).
  useEffect(() => {
    if (!hreflangPath) return;
    const hrefFor = (loc: MarketingLocale) =>
      SITE_ORIGIN + LOCALE_PREFIX[loc] + hreflangPath;
    const canonicalHref = hrefFor(currentLocale);

    // Only emit cross-locale alternates when the page actually exists in more
    // than one locale. Most non-EN reframe bridges are single-locale (no EN
    // sibling) — they get a canonical only, never alternates pointing at 404s.
    const alternates: Array<{ id: string; hreflang: string; href: string }> =
      hreflangLocales.length > 1
        ? [
            ...hreflangLocales.map((loc) => ({
              id: 'hreflang-' + loc,
              hreflang: LOCALE_HREFLANG[loc],
              href: hrefFor(loc),
            })),
            {
              id: 'hreflang-x-default',
              hreflang: 'x-default',
              href: hrefFor(hreflangLocales.includes('en') ? 'en' : currentLocale),
            },
          ]
        : [];

    for (const a of alternates) {
      let link = document.getElementById(a.id) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.id = a.id;
        link.rel = 'alternate';
        document.head.appendChild(link);
      }
      link.hreflang = a.hreflang;
      link.href = a.href;
    }

    // Canonical points at the current-locale URL so duplicate-content
    // signals don't punish either variant. (Without canonical, both
    // /X and /zh/X might be treated as competing pages.)
    let canonical = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement | null;
    const prevCanonical = canonical?.href;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalHref;

    return () => {
      for (const a of alternates) {
        document.getElementById(a.id)?.remove();
      }
      if (canonical && prevCanonical !== undefined) {
        canonical.href = prevCanonical;
      } else {
        canonical?.remove();
      }
    };
  }, [currentLocale, hreflangPath, hreflangLocales]);

  return (
    <div className="hp-root">
      <div className="page paper-tex">
        <header className="top">
          <div className="wrap">
            <a href="/" className="brand">
              Draftlize
            </a>
            <span className="edition">{t('homepage.edition')}</span>
            <nav>
              <a
                href="/pricing"
                aria-current={activeRoute === '/pricing' ? 'page' : undefined}
                style={
                  activeRoute === '/pricing'
                    ? { color: 'var(--ink-0)', fontWeight: 700 }
                    : undefined
                }
              >
                {t('marketing.navPricing')}
              </a>
              <a
                href="/for-pm"
                aria-current={activeRoute === '/for-pm' ? 'page' : undefined}
                style={
                  activeRoute === '/for-pm'
                    ? { color: 'var(--ink-0)', fontWeight: 700 }
                    : undefined
                }
              >
                {t('marketing.navForPm')}
              </a>
              <a
                href="/for-developers"
                aria-current={
                  activeRoute === '/for-developers' ? 'page' : undefined
                }
                style={
                  activeRoute === '/for-developers'
                    ? { color: 'var(--ink-0)', fontWeight: 700 }
                    : undefined
                }
              >
                {t('marketing.navForDevelopers')}
              </a>
            </nav>
            <LanguageToggle />
            {authed ? (
              <a href="/" className="cta">
                {t('marketing.navOpenApp')}
              </a>
            ) : (
              <a href="/" className="cta">
                {t('marketing.navStart')}
              </a>
            )}
          </div>
        </header>

        {children}

        <RelatedLinks hreflangPath={hreflangPath} currentLocale={currentLocale} />

        <footer>
          <div className="foot-top">
            <div className="brand-block">
              <span className="brand serif">Draftlize</span>
              <p>{t('homepage.footerBlurb')}</p>
            </div>
            <div className="col">
              <h6>{t('marketing.footerProduct')}</h6>
              <div className="auth-row" style={{ flexDirection: 'column', gap: 6 }}>
                <a href="/pricing" className="auth">{t('marketing.navPricing')}</a>
                <a href="/for-pm" className="auth">{t('marketing.navForPm')}</a>
                <a href="/for-developers" className="auth">{t('marketing.navForDevelopers')}</a>
                <a href="/" className="auth">{t('marketing.navHome')}</a>
              </div>
            </div>
            <div className="col">
              <h6>{t('homepage.footerSignIn')}</h6>
              <div className="auth-row" style={{ flexDirection: 'column', gap: 6 }}>
                <a href="/api/auth/github/start" className="auth">{t('homepage.footerGitHub')}</a>
                <a href="/api/auth/google/start" className="auth">{t('homepage.footerGoogle')}</a>
                <a href="/login" className="auth">{t('homepage.footerMagicLink')}</a>
              </div>
            </div>
          </div>
          <div className="foot-bot">
            <span>{t('homepage.footerEdition')}</span>
            <span className="nav-min">
              <a href="#">{t('homepage.footerPrivacy')}</a>
              <a href="#">{t('homepage.footerTerms')}</a>
              <a href="mailto:hi@draftlize.com">{t('homepage.footerContact')}</a>
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}
